import { cloneDeep } from "lodash";
import {
  Dispatch,
  SetStateAction,
  createContext,
  createElement,
  useState,
} from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router";
import { Wizard } from "../../../../components";
import { useAxios, useSearchAddress } from "../../../../hooks";
import { User } from "../../../../types";
import Address from "./Address";
import BasicInfo from "./BasicInfo";
import Finalize from "./Finalize";
import UserGroup from "./UserGroup";
import { ValidateAddress } from "../../../../methods";

type Data = User.Create;
type contextType = {
  data: Data;
  setData: Dispatch<SetStateAction<Data>>;
  handleSetValue: (key: keyof Data) => (value: any) => void;
  addressErrorMsg: string;
};

export const Context = createContext({} as contextType);

export default function CreatePlatformUser() {
  const steps = [
    {
      label: "userManagement.platformUsers.create.basicInfoTab",
      text: "userManagement.platformUsers.create.basicInfoDesc",
      id: 0,
      component: BasicInfo,
    },
    {
      label: "userManagement.platformUsers.create.userGroupTab",
      text: "userManagement.platformUsers.create.userGroupDesc",
      id: 1,
      component: UserGroup,
    },
    {
      label: "userManagement.platformUsers.create.addressTab",
      text: "userManagement.platformUsers.create.addressDesc",
      id: 2,
      component: Address,
    },
    {
      label: "userManagement.platformUsers.create.finalizeTab",
      text: "userManagement.platformUsers.create.finalizeDesc",
      id: 3,
      component: Finalize,
    },
  ];
  const navigate = useNavigate();
  const { axios, loading } = useAxios();
  const [activeIndex, setActiveIndex] = useState(0);
  const [data, setData] = useState({} as Data);
  const isFirstStep = activeIndex === 0;
  const isLastStep = activeIndex === steps.length - 1;
  const isLocationStep = activeIndex === 2;
  const [addressErrorMsg, setAddressErrorMasg] = useState("");
  const searchAddress = useSearchAddress();
  const [validatingAddress, setValidatingAddress] = useState(false);

  const handleSetValue = (key: keyof Data) => {
    return (value: any) =>
      setData((p) => {
        const data = cloneDeep(p);
        data[key] = value;
        return data;
      });
  };

  const handleCancel = () => {
    if (isFirstStep) return navigate(-1);
    setActiveIndex((p) => p - 1);
  };

  const validateAddress = async () => {
    setAddressErrorMasg("");
    setValidatingAddress(true);
    await ValidateAddress(data.address as any, searchAddress).then(res => {
      if (!res?.selected && !res.suggested) {
        setAddressErrorMasg("Address is not valid");
      }
      if (res.selected) {
        setData({
          ...data,
          address: {
            ...res.selected,
            name: data.address?.name || '',
            number: data.address?.number || '',
          },
        });
        setActiveIndex((p) => p + 1)
      }
      if (res.suggested) {
        setData({
          ...data,
          address: {
            ...res.suggested,
            name: data.address?.name || '',
            number: data.address?.number || '',
          },
        });
        setAddressErrorMasg(
          "Address has been changed please check before submitting",
        );
      }
    }).finally(() => {
      setValidatingAddress(false);
    });
  }

  const submit = async () => {
    if (isLocationStep && data.address) { await validateAddress() }
    else if (isLastStep) {
      const url = "/accountservice/api/users/platform";
      const body = { ...data };
      axios.post(url, body).then(({ data: id }) => {
        const url = `/platform-users/${id}`;
        const message = "userManagement.platformUsers.create.successMessage";
        toast.success(message);
        navigate(url);
      });
    }
    else return setActiveIndex((p) => p + 1);
  };

  return (
    <Wizard onSubmit={submit} onCancel={handleCancel} activeStep={activeIndex}>
      <Wizard.Items>
        {steps.map((e) => (
          <Wizard.Item
            key={e.id}
            index={e.id}
            label={e.label}
            description={e.text}
          />
        ))}
      </Wizard.Items>
      <Wizard.Tabs>
        <Context.Provider value={{ data, setData, handleSetValue, addressErrorMsg }}>
          {steps.map((e) => (
            <Wizard.Tab
              key={e.id}
              index={e.id}
              label={e.label}
              loading={loading.post || validatingAddress}
              isLastStep={isLastStep}
            >
              {createElement(e.component)}
            </Wizard.Tab>
          ))}
        </Context.Provider>
      </Wizard.Tabs>
    </Wizard>
  );
}
